"use client";

import React, { createContext, useReducer, useCallback, useMemo, useRef } from 'react';
import { 
  DrawerStackContextType, 
  DrawerStackProviderProps, 
  DrawerStackState, 
  DrawerItem 
} from './types.js';
import { drawerStackReducer } from './reducer.js';
import { DrawerRouter } from './router.js';

// Create the context
export const DrawerStackContext = createContext<DrawerStackContextType | null>(null);

export function DrawerStackProvider({
  children,
  maxDepth = 3,
  enableNesting = true,
  mobileOptimization = true
}: DrawerStackProviderProps) {
  const [state, dispatch] = useReducer(drawerStackReducer, { stack: [] });
  
  // Create router instance
  const drawerRouter = useMemo(() => new DrawerRouter(enableNesting, mobileOptimization), [enableNesting, mobileOptimization]);
  
  // Use ref to access current state without causing re-renders
  const stateRef = useRef(state);
  stateRef.current = state;

  // Auto-calculate z-index based on stack position
  const calculateZIndex = useCallback((level: number) => {
    const baseZIndex = 10000;
    return baseZIndex + (level * 100);
  }, []);

  // Determine if a drawer type can nest
  const canNest = useCallback((type: string) => {
    if (!enableNesting) return false;
    
    const nestingRules = {
      'modal': true,
      'sheet': true,
      'bottom-drawer': false, // Bottom drawers shouldn't nest
      'side-drawer': true,
      'fullscreen': false // Fullscreen drawers shouldn't nest
    };
    
    return nestingRules[type as keyof typeof nestingRules] ?? true;
  }, [enableNesting]);

  // Determine if a drawer should replace the current one
  const shouldReplace = useCallback((incoming: Omit<DrawerItem, 'level' | 'timestamp'>) => {
    if (stateRef.current.stack.length === 0) return false;
    
    const lastDrawer = stateRef.current.stack[stateRef.current.stack.length - 1];
    
    // Replace if both are replaceable
    if (incoming.config.replaceable && lastDrawer.config.replaceable) {
      return true;
    }
    
    // Replace if same type and related content
    if (incoming.type === lastDrawer.type && incoming.config.replaceable) {
      return true;
    }
    
    // Replace if mobile optimization suggests it
    if (mobileOptimization && incoming.config.mobileOptimized && lastDrawer.config.mobileOptimized) {
      return true;
    }
    
    return false;
  }, [mobileOptimization]);

  const pushDrawer = useCallback((drawer: Omit<DrawerItem, 'level' | 'timestamp'>) => {
    // Check if we should replace instead of push
    if (shouldReplace(drawer)) {
      dispatch({ type: 'REPLACE_DRAWER', payload: drawer as DrawerItem });
    } else {
      dispatch({ type: 'PUSH_DRAWER', payload: drawer as DrawerItem });
    }
  }, [shouldReplace]);

  const popDrawer = useCallback(() => {
    dispatch({ type: 'POP_DRAWER' });
  }, []);

  const replaceDrawer = useCallback((drawer: Omit<DrawerItem, 'level' | 'timestamp'>) => {
    dispatch({ type: 'REPLACE_DRAWER', payload: drawer as DrawerItem });
  }, []);

  const closeAll = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL' });
  }, []);

  const closeDrawer = useCallback((id: string) => {
    dispatch({ type: 'CLOSE_DRAWER', payload: id });
  }, []);

  const getCurrentDrawer = useCallback(() => {
    return stateRef.current.stack.length > 0 ? stateRef.current.stack[stateRef.current.stack.length - 1] : null;
  }, []);

  const getDrawerLevel = useCallback((id: string) => {
    const drawer = stateRef.current.stack.find(d => d.id === id);
    return drawer ? drawer.level : -1;
  }, []);

  const contextValue: DrawerStackContextType = useMemo(() => ({
    pushDrawer,
    popDrawer,
    replaceDrawer,
    closeAll,
    closeDrawer,
    getCurrentDrawer,
    getDrawerLevel,
    canNest,
    shouldReplace
  }), [
    pushDrawer,
    popDrawer,
    replaceDrawer,
    closeAll,
    closeDrawer,
    getCurrentDrawer,
    getDrawerLevel,
    canNest,
    shouldReplace
  ]);

  return (
    <DrawerStackContext.Provider value={contextValue}>
      {children}
    </DrawerStackContext.Provider>
  );
}
